import { defineStore } from 'pinia'
import { authService } from '@/services/api/index'
import { instance } from '@/services/api/instance'
import { useRestaurantStore } from './restaurant'

// Setzt nach Login (oder beim Reload) den X-Restaurant-Id Default-Header,
// damit alle weiteren API-Calls automatisch im richtigen Tenant landen.
function applyTenantHeader(restaurantId) {
  if (restaurantId) {
    instance.defaults.headers.common['X-Restaurant-Id'] = restaurantId
    localStorage.setItem('current_restaurant_id', restaurantId)
  } else {
    delete instance.defaults.headers.common['X-Restaurant-Id']
    localStorage.removeItem('current_restaurant_id')
  }
}

// Beim ersten Import (Page-Reload) gespeicherte Tenant-Id wieder aktivieren
const persistedRid = localStorage.getItem('current_restaurant_id')
if (persistedRid) instance.defaults.headers.common['X-Restaurant-Id'] = persistedRid

export const useAuthStore = defineStore('auth', {
  state: () => ({
    user: null,
    token: localStorage.getItem('auth_token') || null,
    loading: false,
    error: null,
    isSuperAdmin: false, // Temporär, bis die Rolle vom Backend kommt
  }),
  getters: {
    isAuthenticated: (state) => !!state.token,
    role: (state) => (state.user?.role || '').toLowerCase(),
    isAdmin: (state) => ['admin', 'superadmin', 'hotel_group_admin'].includes((state.user?.role || '').toLowerCase()),
    isPropertyAdmin: (state) => (state.user?.role || '').toLowerCase() === 'admin',
    isManager: (state) => (state.user?.role || '').toLowerCase() === 'manager',
    isHotelGroupAdmin: (state) => (state.user?.role || '').toLowerCase() === 'hotel_group_admin',
    hasSuperAdminRole: (state) => state.isSuperAdmin || (state.user?.role || '').toLowerCase() === 'superadmin',
    /**
     * Capabilities aus dem Backend (single source of truth).
     * Fallback-Logik fuer aeltere Tokens ohne capabilities-Feld.
     */
    capabilities: (state) => state.user?.capabilities ?? {},
    can: (state) => (cap) => {
      const caps = state.user?.capabilities ?? {}
      if (cap in caps) return !!caps[cap]
      const role = (state.user?.role || '').toLowerCase()
      if (cap === 'viewGroupDashboard' || cap === 'manageGroupProperties') {
        return role === 'superadmin' || role === 'hotel_group_admin'
      }
      if (cap === 'manageUsers' || cap === 'manageContent' || cap === 'manageSystemSettings') {
        return ['admin', 'superadmin', 'hotel_group_admin'].includes(role)
      }
      return false
    },
  },
  actions: {
    async login(credentials) {
      this.loading = true
      this.error = null
      
      try {
        // Ausgabe für Debugging
        console.log('Login-Versuch mit:', credentials)
        
        const response = await authService.login({ 
          email: credentials.email, 
          password: credentials.password 
        })
        console.log('Login-Antwort:', response)
        
        this.token = response.data.token
        this.user = response.data.user
        
        // SuperAdmin-Status basierend auf der Benutzerrolle setzen - immer Kleinbuchstaben vergleichen
        const loginUserRole = this.user?.role?.toLowerCase() || '';
        this.isSuperAdmin = loginUserRole === 'superadmin';
        console.log('SuperAdmin-Status gesetzt auf:', this.isSuperAdmin, 'für Rolle:', loginUserRole);
        
        localStorage.setItem('auth_token', this.token)
        localStorage.setItem('user', JSON.stringify(this.user))

        // Tenant-Kontext setzen (Multi-Tenant): künftige API-Calls bekommen X-Restaurant-Id automatisch
        applyTenantHeader(this.user?.restaurantId ?? this.user?.restaurant_id ?? null)

        // WebSocket-Initialisierung wird automatisch über den Auth-Watcher in App.vue gestartet
        
        // Nach erfolgreicher Anmeldung Restaurantdaten laden
        if (loginUserRole === 'admin' || loginUserRole === 'superadmin') {
          const restaurantStore = useRestaurantStore()
          await restaurantStore.fetchRestaurantSettings()
        }
        
        return true
      } catch (err) {
        console.error('Fehler beim Login:', err)
        this.error = 'Anmeldung fehlgeschlagen. Bitte überprüfen Sie Ihre Zugangsdaten.'
        return false
      } finally {
        this.loading = false
      }
    },
    async logout() {
      try {
        await authService.logout()
      } catch (err) {
        console.error('Fehler beim Logout:', err)
      } finally {
        const restaurantStore = useRestaurantStore()
        restaurantStore.reset()
        this.token = null
        this.user = null
        this.isSuperAdmin = false
        localStorage.removeItem('auth_token')
        localStorage.removeItem('user')
        applyTenantHeader(null)
      }
    },
    async changePassword(data) {
      try {
        await authService.changePassword(data)
      } catch (err) {
        const msg = err.response?.data?.message || 'Fehler beim Ändern des Passworts'
        throw new Error(msg)
      }
    },
    async fetchUser() {
      try {
        const response = await authService.getUser()
        this.user = response.data
        localStorage.setItem('user', JSON.stringify(this.user))
      } catch (err) {
        console.error('Fehler beim Laden des Benutzers:', err)
      }
    },
    initializeAuth() {
      const savedToken = localStorage.getItem('auth_token'); // Lese auch den Token
      const savedUser = localStorage.getItem('user');
      
      // Setze den Token-Status direkt
      if (savedToken) {
          this.token = savedToken;
      }

      if (savedUser) {
        try {
          // Prüfe ob savedUser ein gültiger JSON-String ist
          if (savedUser !== 'undefined' && savedUser !== 'null' && savedUser.trim() !== '') {
          this.user = JSON.parse(savedUser);
          } else {
            // Ungültige Daten löschen
            localStorage.removeItem('user');
            this.user = null;
            return;
          }
          
          // SuperAdmin-Status basierend auf der Benutzerrolle setzen - immer Kleinbuchstaben vergleichen
          const userRole = this.user?.role?.toLowerCase() || '';
          this.isSuperAdmin = userRole === 'superadmin';
          console.log('SuperAdmin-Status bei Initialisierung gesetzt auf:', this.isSuperAdmin, 'für Rolle:', userRole);
          
          // --- ENTFERNT --- 
          // Wenn Benutzer bereits angemeldet ist, Restaurantdaten laden
          // => Dies sollte erst passieren, wenn sicher ist, dass der Token noch gültig ist,
          //    z.B. nach der ersten Navigation oder explizit in main.js nach Prüfung.
          // if (this.user && (this.user.role === 'Admin' || this.user.role === 'SuperAdmin')) {
          //   const restaurantStore = useRestaurantStore()
          //   restaurantStore.fetchRestaurantSettings() 
          // }
          // --- ENDE ENTFERNT ---

        } catch (e) {
          console.error('Fehler beim Parsen des gespeicherten Benutzers:', e);
          // Bei Fehler User/Token löschen, um inkonsistenten Zustand zu vermeiden
          this.user = null;
          this.token = null;
          localStorage.removeItem('user');
          localStorage.removeItem('auth_token');
        }
      } else {
        // Wenn kein User gespeichert ist, sicherstellen, dass auch kein Token mehr da ist
        // (Sollte durch Logout passieren, aber zur Sicherheit)
         if(this.token){ // Nur wenn noch ein Token im State wäre
             localStorage.removeItem('auth_token');
             this.token = null;
         }
      }
    }
  },
}) 